import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

const TeacherSchedule = () => {
  const routes = all_routes;
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [viewType, setViewType] = useState<"daily" | "weekly">("weekly");

  // Mock data with dates for weekly view
  const mockScheduleData = [
    {
      id: 1,
      date: "2024-08-12",
      time: "09:00 - 09:45",
      class: "Grade 10A",
      subject: "Mathematics",
      room: "Room 201",
      students: 30,
      status: "Completed",
    },
    {
      id: 2,
      date: "2024-08-12",
      time: "10:00 - 10:45",
      class: "Grade 11B",
      subject: "Physics",
      room: "Lab 1",
      students: 28,
      status: "Ongoing",
    },
    {
      id: 3,
      date: "2024-08-12",
      time: "11:00 - 11:45",
      class: "Grade 9C",
      subject: "Mathematics",
      room: "Room 105",
      students: 32,
      status: "Upcoming",
    },
    {
      id: 4,
      date: "2024-08-12",
      time: "14:00 - 14:45",
      class: "Grade 12A",
      subject: "Advanced Math",
      room: "Room 301",
      students: 25,
      status: "Scheduled",
    },
    {
      id: 5,
      date: "2024-08-13",
      time: "09:00 - 09:45",
      class: "Grade 10B",
      subject: "Mathematics",
      room: "Room 202",
      students: 28,
      status: "Scheduled",
    },
    {
      id: 6,
      date: "2024-08-13",
      time: "10:00 - 10:45",
      class: "Grade 11A",
      subject: "Physics",
      room: "Lab 2",
      students: 30,
      status: "Scheduled",
    },
    {
      id: 7,
      date: "2024-08-14",
      time: "09:00 - 09:45",
      class: "Grade 12B",
      subject: "Advanced Math",
      room: "Room 302",
      students: 26,
      status: "Scheduled",
    },
  ];

  const getWeekDates = (date: string) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    const sunday = new Date(d.setDate(diff));

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const weekDay = new Date(sunday);
      weekDay.setDate(sunday.getDate() + i);
      weekDates.push(weekDay.toISOString().split("T")[0]);
    }
    return weekDates;
  };

  const getCurrentSchedule = () => {
    if (viewType === "daily") {
      return mockScheduleData.filter((item) => item.date === selectedDate);
    } else {
      const weekDates = getWeekDates(selectedDate);
      return mockScheduleData.filter((item) => weekDates.includes(item.date));
    }
  };

  const groupScheduleByDate = () => {
    const schedule = getCurrentSchedule();
    return schedule.reduce((groups, item) => {
      if (!groups[item.date]) groups[item.date] = [];
      groups[item.date].push(item);
      return groups;
    }, {} as Record<string, typeof mockScheduleData>);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Ongoing":
        return "badge-soft-warning";
      case "Upcoming":
        return "badge-soft-info";
      case "Completed":
        return "badge-soft-success";
      case "Scheduled":
        return "badge-soft-primary";
      case "Cancelled":
        return "badge-soft-danger";
      default:
        return "badge-soft-secondary";
    }
  };

  const currentSchedule = getCurrentSchedule();
  const todayStats = {
    total: currentSchedule.length,
    completed: currentSchedule.filter((s) => s.status === "Completed").length,
    upcoming: currentSchedule.filter(
      (s) => s.status === "Upcoming" || s.status === "Scheduled"
    ).length,
    ongoing: currentSchedule.filter((s) => s.status === "Ongoing").length,
  };

  const formatDateHeader = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const navigateWeek = (direction: "prev" | "next") => {
    const currentDate = new Date(selectedDate);
    const days = direction === "next" ? 7 : -7;
    currentDate.setDate(currentDate.getDate() + days);
    setSelectedDate(currentDate.toISOString().split("T")[0]);
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split("T")[0]);
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={routes.teacherDashboard}>Teacher Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">My Schedule</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="row">
            <div className="col-xl-3 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-lg bg-primary-transparent me-3">
                      <i className="ti ti-calendar"></i>
                    </div>
                    <div>
                      <h4>{todayStats.total}</h4>
                      <p className="text-muted mb-0">Total Classes</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-lg bg-success-transparent me-3">
                      <i className="ti ti-check"></i>
                    </div>
                    <div>
                      <h4>{todayStats.completed}</h4>
                      <p className="text-muted mb-0">Completed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-lg bg-warning-transparent me-3">
                      <i className="ti ti-clock"></i>
                    </div>
                    <div>
                      <h4>{todayStats.ongoing}</h4>
                      <p className="text-muted mb-0">Current Class</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-lg bg-info-transparent me-3">
                      <i className="ti ti-clock-plus"></i>
                    </div>
                    <div>
                      <h4>{todayStats.upcoming}</h4>
                      <p className="text-muted mb-0">Upcoming</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title">My Teaching Schedule</h4>
                  <div className="d-flex gap-2">
                    <button className="btn btn-primary btn-sm">
                      <i className="ti ti-bell me-1"></i>Set Reminder
                    </button>
                    <button className="btn btn-outline-primary btn-sm">
                      <i className="ti ti-download me-1"></i>Export
                    </button>
                  </div>
                </div>

                {/* Date Selection and View Type */}
                <div className="card-body border-bottom">
                  <div className="row g-3 align-items-end">
                    <div className="col-md-3">
                      <label className="form-label">Select Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">View Type</label>
                      <div className="btn-group w-100">
                        <button
                          className={`btn ${
                            viewType === "daily"
                              ? "btn-primary"
                              : "btn-outline-primary"
                          }`}
                          onClick={() => setViewType("daily")}
                        >
                          Daily
                        </button>
                        <button
                          className={`btn ${
                            viewType === "weekly"
                              ? "btn-primary"
                              : "btn-outline-primary"
                          }`}
                          onClick={() => setViewType("weekly")}
                        >
                          Weekly
                        </button>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Navigation</label>
                      <div className="btn-group w-100">
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => navigateWeek("prev")}
                        >
                          <i className="ti ti-chevron-left"></i> Previous
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => navigateWeek("next")}
                        >
                          Next <i className="ti ti-chevron-right"></i>
                        </button>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <button
                        className="btn btn-outline-secondary w-100"
                        onClick={goToToday}
                      >
                        <i className="ti ti-calendar-today me-1"></i>Today
                      </button>
                    </div>
                  </div>
                </div>

                {/* TODO */}
                <div className="card-body border-bottom">
                  <div className="alert alert-info">
                    <h6>
                      <i className="ti ti-info-circle me-2"></i>TODO: Connect to
                      teacher schedule API
                    </h6>
                    <ul className="mb-0">
                      <li>Fetch teacher's assigned classes</li>
                      <li>Mark class attendance directly</li>
                      <li>Update class status and notes</li>
                      <li>Set reminders and notifications</li>
                    </ul>
                  </div>
                </div>

                {/* Schedule Content */}
                <div className="card-body">
                  <div className="schedule-content">
                    {viewType === "daily" ? (
                      // Daily View
                      <div className="daily-schedule">
                        <h5 className="text-primary mb-3 border-bottom pb-2">
                          {formatDateHeader(selectedDate)}
                        </h5>
                        <div className="timeline-schedule">
                          {getCurrentSchedule().length > 0 ? (
                            getCurrentSchedule().map((item) => (
                              <div
                                key={item.id}
                                className={`schedule-item ${item.status.toLowerCase()}`}
                              >
                                <div className="row align-items-center p-3 mb-3 border rounded">
                                  <div className="col-md-2">
                                    <div className="time-block text-center">
                                      <strong className="d-block">
                                        {item.time.split(" - ")[0]}
                                      </strong>
                                      <small className="text-muted">
                                        {item.time.split(" - ")[1]}
                                      </small>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="class-info">
                                      <h6 className="mb-1">{item.subject}</h6>
                                      <p className="text-muted mb-0">
                                        {item.class} • {item.room} •{" "}
                                        {item.students} students
                                      </p>
                                    </div>
                                  </div>
                                  <div className="col-md-2">
                                    <span
                                      className={`badge ${getStatusColor(
                                        item.status
                                      )}`}
                                    >
                                      {item.status}
                                    </span>
                                  </div>
                                  <div className="col-md-2">
                                    <div className="dropdown">
                                      <button
                                        className="btn btn-outline-primary btn-sm dropdown-toggle"
                                        data-bs-toggle="dropdown"
                                      >
                                        Actions
                                      </button>
                                      <ul className="dropdown-menu">
                                        <li>
                                          <Link
                                            to={routes.attendanceTracker}
                                            className="dropdown-item"
                                          >
                                            <i className="ti ti-user-check me-2"></i>
                                            Take Attendance
                                          </Link>
                                        </li>
                                        <li>
                                          <button className="dropdown-item">
                                            <i className="ti ti-notes me-2"></i>
                                            Class Notes
                                          </button>
                                        </li>
                                        <li>
                                          <button className="dropdown-item">
                                            <i className="ti ti-bell me-2"></i>
                                            Notify Students
                                          </button>
                                        </li>
                                        {item.status === "Scheduled" && (
                                          <li>
                                            <button className="dropdown-item text-danger">
                                              <i className="ti ti-x me-2"></i>
                                              Cancel Class
                                            </button>
                                          </li>
                                        )}
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4">
                              <div className="avatar avatar-xl bg-secondary-transparent mb-3">
                                <i className="ti ti-calendar-x fs-1 text-secondary"></i>
                              </div>
                              <h5>No classes scheduled</h5>
                              <p className="text-muted">
                                No classes found for the selected date.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      // Weekly View
                      <div className="weekly-schedule">
                        <h5 className="text-primary mb-3 border-bottom pb-2">
                          Week of{" "}
                          {formatDateHeader(getWeekDates(selectedDate)[0])}
                        </h5>
                        {Object.entries(groupScheduleByDate()).length > 0 ? (
                          Object.entries(groupScheduleByDate()).map(
                            ([date, classes]) => (
                              <div key={date} className="mb-4">
                                <h6 className="text-secondary mb-3 border-bottom pb-1">
                                  {formatDateHeader(date)}
                                </h6>
                                <div className="row g-3">
                                  {classes.map((item) => (
                                    <div key={item.id} className="col-lg-6">
                                      <div className="card border">
                                        <div className="card-body p-3">
                                          <div className="d-flex align-items-start justify-content-between mb-2">
                                            <div>
                                              <h6 className="mb-1">
                                                {item.subject}
                                              </h6>
                                              <p className="text-muted mb-0 small">
                                                {item.time} • {item.class}
                                              </p>
                                              <p className="text-muted mb-0 small">
                                                {item.room} • {item.students}{" "}
                                                students
                                              </p>
                                            </div>
                                            <span
                                              className={`badge ${getStatusColor(
                                                item.status
                                              )}`}
                                            >
                                              {item.status}
                                            </span>
                                          </div>
                                          <div className="d-flex gap-2">
                                            <Link
                                              to={routes.attendanceTracker}
                                              className="btn btn-outline-primary btn-sm flex-fill"
                                            >
                                              <i className="ti ti-user-check me-1"></i>
                                              Attendance
                                            </Link>
                                            <button className="btn btn-outline-secondary btn-sm flex-fill">
                                              <i className="ti ti-notes me-1"></i>
                                              Notes
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          )
                        ) : (
                          <div className="text-center py-4">
                            <div className="avatar avatar-xl bg-secondary-transparent mb-3">
                              <i className="ti ti-calendar-x fs-1 text-secondary"></i>
                            </div>
                            <h5>No classes scheduled</h5>
                            <p className="text-muted">
                              No classes found for the selected week.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TeacherSchedule;
